import { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { User, Mail } from 'lucide-react';
import { fetchStaffProfile } from '../api/staff.js';
import { getStoredStaffSession, saveStaffSession } from '../lib/auth.js';
import { buildStaffSurveyQrUrl } from '../config/surveyUrl.js';

function Profile() {
  const [staff, setStaff] = useState(() => getStoredStaffSession()?.staff ?? null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showQrModal, setShowQrModal] = useState(false);

  useEffect(() => {
    void (async () => {
      const result = await fetchStaffProfile();
      setLoading(false);
      if (!result.ok) {
        setError(result.error || 'Failed to load profile');
        return;
      }
      setStaff(result.staff);
      const session = getStoredStaffSession();
      if (session) {
        saveStaffSession({ ...session, staff: result.staff });
      }
    })();
  }, []);

  if (loading) {
    return <div className="p-4 text-center text-gray-500">Loading...</div>;
  }

  if (error) {
    return <div className="p-4 text-center text-red-600">{error}</div>;
  }

  const surveyUrl = buildStaffSurveyQrUrl(staff?.staffCode);

  return (
    <div className="p-4">
      <div className="mb-6 rounded-lg bg-white p-6 shadow-lg">
        {/* QR code — centered (no tribe section) */}
        <div className="mb-4 flex justify-center">
          {surveyUrl ? (
            <button
              type="button"
              onClick={() => setShowQrModal(true)}
              className="flex cursor-pointer items-center justify-center transition hover:opacity-80"
              style={{ height: '144px', width: '144px' }}
              aria-label="View QR code"
            >
              <QRCodeSVG value={surveyUrl} size={144} level="H" includeMargin />
            </button>
          ) : (
            <div
              className="flex items-center justify-center text-xs text-gray-400"
              style={{ height: '144px', width: '144px' }}
            >
              QR not available
            </div>
          )}
        </div>

        {/* Name & email only */}
        <div className="space-y-4">
          <div className="flex items-center gap-3 rounded-lg bg-gray-50 p-3">
            <User className="text-brand-accent" size={24} />
            <div className="flex-1">
              <div className="text-xs text-gray-500">Name</div>
              <div className="font-semibold text-gray-800">{staff?.name ?? '—'}</div>
            </div>
          </div>

          <div className="flex items-center gap-3 rounded-lg bg-gray-50 p-3">
            <Mail className="text-brand-accent" size={24} />
            <div>
              <div className="text-xs text-gray-500">Email</div>
              <div className="font-semibold text-gray-800">{staff?.email || '—'}</div>
            </div>
          </div>
        </div>
      </div>

      {surveyUrl && (
        <p className="break-all text-center text-xs text-gray-400">{surveyUrl}</p>
      )}

      {/* QR modal */}
      {showQrModal && surveyUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          onClick={() => setShowQrModal(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-sm rounded-lg bg-white p-6"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="qr-modal-title"
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 id="qr-modal-title" className="text-xl font-semibold text-gray-800">
                QR Code
              </h3>
              <button
                type="button"
                onClick={() => setShowQrModal(false)}
                className="text-2xl font-bold text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="flex justify-center rounded-lg border-4 border-gray-200 bg-white p-6">
              <QRCodeSVG value={surveyUrl} size={280} level="H" includeMargin />
            </div>
            <p className="mt-4 text-center text-sm text-gray-600">
              Ask customers to scan this code to start the survey
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;
